import {
  BOTTOM,
  DARK,
  GRAY,
  LIGHT,
  PAGE,
  RADIUS,
  WHITE,
  attendanceKinds,
  attendanceTone,
  cardTable,
  condensed,
  courseCell,
  drawCourseCell,
  drawLogo,
  fullName,
  gradeText,
  gradeTone,
  number,
  periodAttendance,
  periodComment,
  periodGpa,
  periodGrade,
  periodHead,
  pill,
  sectionGap,
  shortPeriodName,
  tint,
} from './shared'

const BAND = 60
const TOP = BAND + 24
const SIDE_WIDTH = 146
const MAIN_LEFT = PAGE.margin + SIDE_WIDTH + 18
const MAIN_WIDTH = PAGE.width - PAGE.margin - MAIN_LEFT
const GPA_SCALE = 4

const band = (doc, ctx) => {
  doc.setFillColor(...ctx.primary)
  doc.rect(0, 0, PAGE.width, BAND, 'F')
  doc.setFillColor(...tint(ctx.primary, 0.55))
  doc.rect(0, BAND, PAGE.width, 3, 'F')

  if (ctx.logo) {
    const height = 28
    const width = Math.min((ctx.logo.width / ctx.logo.height) * height, 130)
    doc.setFillColor(...WHITE)
    doc.roundedRect(PAGE.margin, (BAND - height - 10) / 2, width + 14, height + 10, 6, 6, 'F')
    drawLogo(doc, ctx.logo, PAGE.margin + 7, (BAND - height) / 2, width, height)
  }
  doc.setTextColor(...WHITE)
  doc.setFont('helvetica', 'bold')
  doc.setFontSize(13)
  doc.text(ctx.schoolName ?? '', PAGE.width - PAGE.margin, 28, { align: 'right' })
  doc.setFont('helvetica', 'normal')
  doc.setFontSize(8.5)
  doc.text(ctx.subtitle, PAGE.width - PAGE.margin, 42, { align: 'right' })
}

const label = (doc, text, x, y) => {
  doc.setFont('helvetica', 'bold')
  doc.setFontSize(6)
  doc.setTextColor(...GRAY)
  doc.text(text.toUpperCase(), x, y)
}

const bar = (doc, x, y, width, ratio, color) => {
  doc.setFillColor(...LIGHT)
  doc.roundedRect(x, y, width, 4, 2, 2, 'F')
  const filled = Math.max(0, Math.min(1, ratio ?? 0)) * width
  if (filled > 0) {
    doc.setFillColor(...color)
    doc.roundedRect(x, y, Math.max(filled, 4), 4, 2, 2, 'F')
  }
}

const sidebar = (doc, card, ctx) => {
  const { t, primary } = ctx
  const x = PAGE.margin + 12
  const width = SIDE_WIDTH - 24
  const kinds = attendanceKinds(t)
  doc.setFont('helvetica', 'bold')
  doc.setFontSize(12)
  const nameLines = doc.splitTextToSize(fullName(card.student), width)

  const statBlock = 16 + 30 + 10
  const height =
    18 +
    nameLines.length * 14 +
    6 +
    ctx.fields.length * 20 +
    (ctx.hasGpa ? statBlock + ctx.periods.length * 12 : 0) +
    statBlock +
    kinds.length * 12 +
    6
  doc.setFillColor(...WHITE)
  doc.setDrawColor(...tint(primary, 0.8))
  doc.setLineWidth(0.7)
  doc.roundedRect(PAGE.margin, TOP, SIDE_WIDTH, Math.min(height, BOTTOM - TOP), RADIUS + 2, RADIUS + 2, 'FD')
  doc.setFillColor(...primary)
  doc.roundedRect(PAGE.margin + 12, TOP, 28, 3, 1.5, 1.5, 'F')

  let y = TOP + 22
  doc.setTextColor(...DARK)
  doc.text(nameLines, x, y)
  y += nameLines.length * 14 + 2

  ctx.fields.forEach(([fieldLabel, value]) => {
    label(doc, fieldLabel, x, y)
    doc.setFont('helvetica', 'normal')
    doc.setFontSize(8.5)
    doc.setTextColor(...DARK)
    doc.text(String(value), x, y + 10)
    y += 20
  })

  const rule = () => {
    doc.setDrawColor(...LIGHT)
    doc.setLineWidth(0.6)
    doc.line(x, y, x + width, y)
    y += 16
  }

  const statRow = (text, value, color) => {
    if (color) {
      doc.setFillColor(...color)
      doc.circle(x + 2, y - 2.5, 2, 'F')
    }
    doc.setFont('helvetica', 'normal')
    doc.setFontSize(8)
    doc.setTextColor(...GRAY)
    doc.text(text, color ? x + 8 : x, y)
    doc.setFont('helvetica', 'bold')
    doc.setTextColor(...DARK)
    doc.text(String(value), x + width, y, { align: 'right' })
    y += 12
  }

  const headline = (title, value, color, ratio) => {
    rule()
    label(doc, title, x, y)
    doc.setFont('helvetica', 'bold')
    doc.setFontSize(20)
    doc.setTextColor(...color)
    doc.text(value, x, y + 20)
    bar(doc, x, y + 27, width, ratio, color)
    y += 30 + 14
  }

  if (ctx.hasGpa) {
    headline(t('Cumulative GPA'), number(card.gpa?.cumulative), primary, (card.gpa?.cumulative ?? 0) / GPA_SCALE)
    ctx.periods.forEach(period => statRow(shortPeriodName(period.name), number(periodGpa(card.gpa, period))))
  }

  const rate = ctx.attendanceRate
  headline(t('Attendance'), rate == null ? '—' : `${rate}%`, attendanceTone(rate), rate == null ? 0 : rate / 100)
  kinds.forEach(kind => statRow(kind.label, ctx.attendance.total?.[kind.key] ?? '—', kind.tone))
}

const mainTitle = (doc, title, y, primary) => {
  const text = title.toUpperCase()
  doc.setFont('helvetica', 'bold')
  doc.setFontSize(8.5)
  doc.setTextColor(...primary)
  doc.text(text, MAIN_LEFT, y)
  const end = MAIN_LEFT + doc.getTextWidth(text) + 8
  doc.setDrawColor(...tint(primary, 0.75))
  doc.setLineWidth(0.5)
  doc.line(end, y - 3, PAGE.width - PAGE.margin, y - 3)
  return y + 8
}

export default {
  limit: BOTTOM,
  render(doc, card, ctx, density) {
    const { t, primary, periods, selected } = ctx
    const d = condensed(density)
    const look = { headFill: tint(primary, 0.9), headText: primary, outline: tint(primary, 0.82), divider: LIGHT }
    band(doc, ctx)
    sidebar(doc, card, ctx)

    const columns = periods.length + 1
    const body = []
    ;(card.courses ?? []).forEach(course => {
      body.push([
        courseCell(course, d),
        ...periods.map(period => ({ content: periodGrade(course, period), kind: 'grade' })),
        { content: gradeText(course.finalGrade) ?? '—', kind: 'grade', final: true },
      ])
      const comment = d.comments && periodComment(course, selected)
      if (comment) {
        body.push([
          {
            content: `“${comment}”`,
            colSpan: columns + 1,
            kind: 'comment',
            styles: { fontStyle: 'italic', fontSize: d.commentFont, textColor: GRAY, cellPadding: { top: 0, bottom: d.pad + 2, left: 18, right: 8 } },
          },
        ])
      }
    })

    let y = mainTitle(doc, t('Grades'), TOP + 8, primary)
    y = cardTable(doc, d, look, {
      startY: y,
      top: TOP,
      x: MAIN_LEFT,
      width: MAIN_WIDTH,
      head: [[t('Course'), ...periods.map(period => periodHead(period.name)), periodHead(t('Final'))]],
      body: body.length ? body : [[{ content: t('No courses'), colSpan: columns + 1, styles: { textColor: GRAY } }]],
      columnStyles: Object.fromEntries(Array.from({ length: columns }, (_, i) => [i + 1, { halign: 'center', valign: 'middle', cellWidth: 36 }])),
      skipDivider: data => data.table.body[data.row.index + 1]?.raw?.[0]?.kind === 'comment',
      willDrawCell: data => {
        const kind = data.cell.raw?.kind
        if (data.section === 'body' && (kind === 'course' || (kind === 'grade' && data.cell.raw.content !== '—'))) data.cell.text = []
      },
      didDrawCell: data => {
        if (data.section !== 'body') return
        const { cell } = data
        const kind = cell.raw?.kind
        if (kind === 'course') drawCourseCell(doc, cell, d)
        if (kind === 'grade' && cell.raw.content !== '—') {
          pill(doc, String(cell.raw.content), cell.x + cell.width / 2, cell.y + cell.height / 2, cell.raw.final ? primary : gradeTone(cell.raw.content), Math.min(d.pill, 12))
        }
        if (kind === 'comment') {
          doc.setDrawColor(...tint(primary, 0.5))
          doc.setLineWidth(1.5)
          doc.line(cell.x + 10, cell.y + 1, cell.x + 10, cell.y + cell.height - d.pad - 1)
        }
      },
    })

    y = mainTitle(doc, t('Attendance by Marking Period'), y + sectionGap(d) + 4, primary)
    const kinds = attendanceKinds(t)
    return cardTable(doc, d, look, {
      startY: y,
      top: TOP,
      x: MAIN_LEFT,
      width: MAIN_WIDTH,
      head: [[t('Marking Period'), ...kinds.map(kind => ({ content: kind.label, styles: { halign: 'center' } }))]],
      body: periods.map(period => {
        const row = periodAttendance(ctx.attendance, period)
        return [period.name, ...kinds.map(kind => ({ content: row[kind.key] ?? '—', styles: { halign: 'center' } }))]
      }),
    })
  },
}
